/**
 * SHELL — today's course + simulation provider with fixture fallbacks.
 *
 * Course: try Core's real `generateCourse(seed, dateKey)`; until Core lands it
 * throws, so we fall back to the fixture `SAMPLE_COURSE` (re-stamped with today's
 * seed/dateKey) so the whole flow runs. Swapping to real generation is automatic
 * once Core fills in the body.
 *
 * Simulation: default factory uses `createFakeSimulation` from @/fixtures. To go
 * live with real physics, pass `createSimulation` from @/core at boot — ONE line,
 * no other call-site changes, because both satisfy the `Simulation` contract.
 */

import { SAMPLE_COURSE, createFakeSimulation } from '@/fixtures';
import { deriveDailySeed } from '@/types';
import type { CourseSpec, DateKey } from '@/types';
import type { Simulation } from '@/core';
import { generateCourse } from '@/core';
import type { SimulationFactory } from '../app';

/** Resolve today's course for the given date key, with a fixture fallback. */
export function resolveCourse(dateKey: DateKey): { course: CourseSpec; real: boolean } {
  const seed = deriveDailySeed(dateKey);
  try {
    return { course: generateCourse(seed, dateKey), real: true };
  } catch {
    // Re-stamp the fixture so dateKey/seed are coherent for this run.
    const course: CourseSpec = { ...SAMPLE_COURSE, seed, dateKey };
    return { course, real: false };
  }
}

/**
 * The default simulation factory (fixtures). Boot can override with @/core's
 * `createSimulation` once Core is ready. Returns a promise to match the real
 * async (wasm) factory signature.
 */
export const defaultSimulationFactory: SimulationFactory = (spec: CourseSpec): Promise<Simulation> =>
  Promise.resolve(createFakeSimulation(spec));
